import React, { useState, useMemo, useEffect, useRef } from 'react';

const HIGH_COPAY_ALERT_THRESHOLD = 11;

export const CopayPaymentStatus = ({
    rxData,
    index,
    rxnumber,
    copayValue,
    winrxDataId,
    onPaymentChange
}) => {
    const [paymentStatus, setPaymentStatus] = useState(null);
    const [note, setNote] = useState('');
    const prevPaymentDataRef = useRef(null);
    const onPaymentChangeRef = useRef(onPaymentChange);

    // Keep ref updated with latest callback
    useEffect(() => {
        onPaymentChangeRef.current = onPaymentChange;
    }, [onPaymentChange]);

    const parsedCopay = useMemo(() => {
        const value = parseFloat(copayValue);
        return !isNaN(value) && value >= HIGH_COPAY_ALERT_THRESHOLD ? value : null;
    }, [copayValue]);

    // Notify parent when payment status changes - only when values actually change
    useEffect(() => {
        if (!onPaymentChangeRef.current || !winrxDataId || !parsedCopay) {
            return;
        }

        let currentPaymentData = null;
        if (paymentStatus === 'yes' || paymentStatus === 'no') {
            currentPaymentData = {
                winrx_data_id: winrxDataId,
                status: paymentStatus,
                comment: note || ''
            };
        }

        // Only call onPaymentChange if the data actually changed
        const prevData = prevPaymentDataRef.current;
        if (
            prevData?.status !== currentPaymentData?.status ||
            prevData?.comment !== currentPaymentData?.comment ||
            (prevData === null && currentPaymentData !== null) ||
            (prevData !== null && currentPaymentData === null)
        ) {
            prevPaymentDataRef.current = currentPaymentData;
            onPaymentChangeRef.current(winrxDataId, currentPaymentData);
        }
    }, [paymentStatus, note, winrxDataId, parsedCopay]);

    const handlePaymentStatusChange = (e) => {
        const value = e.target.value;
        setPaymentStatus(value);
        if (!value) {
            setNote('');
        }
    };

    const showNoteInput = paymentStatus === 'yes' || paymentStatus === 'no';
    const radioName = `payment-status-${index}-${rxnumber}`;

    // Only show the driver alert for high copays.
    if (!parsedCopay) {
        return null;
    }

    return (
        <div className="mt-1.5 bg-gradient-to-br from-red-50 to-rose-50 rounded-lg p-2 border border-red-200 shadow-sm">
            {/* Copay Badge and Payment Status in one row */}
            <div className="flex items-center justify-between gap-2 flex-wrap">
                {/* Copay Badge */}
                <span className="inline-flex items-center gap-1 px-1.5 py-1 font-bold text-red-700">
                    Collect ${parsedCopay.toFixed(2)}
                </span>

                {/* Payment Status Selection */}
                <div className="flex items-center gap-1.5">
                   
                    <div className="flex items-center gap-1 bg-white rounded-full px-1 py-0.5 border border-gray-200 shadow-sm">
                        {[
                            { value: 'yes', label: 'YES', inputClass: 'text-green-600 focus:ring-green-500', labelClass: 'text-green-700' },
                            { value: 'no', label: 'NO', inputClass: 'text-red-600 focus:ring-red-500', labelClass: 'text-red-700' }
                        ].map((option, i) => (
                            <React.Fragment key={option.value}>
                                
                                <div className="flex items-center px-2 py-1">
                                    <input
                                        type="radio"
                                        name={radioName}
                                        id={`payment-${option.value}-${index}-${rxnumber}`}
                                        value={option.value}
                                        checked={paymentStatus === option.value}
                                        onChange={handlePaymentStatusChange}
                                        className={`w-3 h-3  bg-gray-100 border-gray-300 focus:ring-1 cursor-pointer`}
                                    />
                                    <label
                                        htmlFor={`payment-${option.value}-${index}-${rxnumber}`}
                                        className={`ml-1  text-sm font-bold cursor-pointer text-black transition-colors`}
                                    >
                                        {option.label}
                                    </label>
                                </div>
                            </React.Fragment>
                        ))}
                    </div>
                </div>
            </div>

            {/* Note Input - Show when YES or NO is selected */}
            {showNoteInput && (
                <div className="mt-1.5">
                    <input
                        type="text"
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="Add note..."
                        className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm bg-white focus:ring-1 focus:ring-red-400 focus:border-red-400 transition-all placeholder:text-gray-400"
                    />
                </div>
            )}
        </div>
    );
};
